import { useCallback, useEffect, useRef } from 'react';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import type { VendorPortalUserPrefs } from '../../types';
import { logVendorPortalActivity } from '../../utils/vendorActivityLogger';
import {
  markVendorPortalTourCompleted,
  markVendorPortalTourSkipped,
} from '../../utils/vendorPortalUserPrefs';
import { shouldAutoStartTour } from '../../utils/vendorPortalTourPrefs';
import { buildVendorPortalTourSteps } from '../../utils/vendorPortalTourSteps';
import './VendorPortalTour.css';

export type UseVendorPortalTourOptions = {
  userId: string | undefined;
  orgId: string | undefined;
  showTimber: boolean;
  showCnc: boolean;
  projectCount: number;
  readOnlyPreview: boolean;
  realIsSupplier: boolean;
  prefs: VendorPortalUserPrefs | null | undefined;
  prefsLoaded: boolean;
  portalReady: boolean;
};

export type StartVendorPortalTourOptions = {
  manual?: boolean;
};

function missingTourTargets(steps: ReturnType<typeof buildVendorPortalTourSteps>): string[] {
  const missing: string[] = [];
  for (const step of steps) {
    if (typeof step.element !== 'string') continue;
    if (!document.querySelector(step.element)) {
      missing.push(step.element);
    }
  }
  return missing;
}

export function useVendorPortalTour(options: UseVendorPortalTourOptions) {
  const {
    userId,
    orgId,
    showTimber,
    showCnc,
    projectCount,
    readOnlyPreview,
    realIsSupplier,
    prefs,
    prefsLoaded,
    portalReady,
  } = options;

  const driverRef = useRef<Driver | null>(null);
  const autoStartedRef = useRef(false);
  const finishedRef = useRef(false);

  const destroyTour = useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = null;
  }, []);

  const startTour = useCallback(
    (startOpts?: StartVendorPortalTourOptions) => {
      if (!userId || !orgId || projectCount === 0) return false;

      destroyTour();
      finishedRef.current = false;

      const steps = buildVendorPortalTourSteps({ showTimber, showCnc });
      const missing = missingTourTargets(steps);
      if (missing.length > 0) {
        console.warn('[VendorPortalTour] Missing tour targets:', missing);
        return false;
      }

      void logVendorPortalActivity(orgId, 'page_view', 'tour_started', {
        metadata: { manual: !!startOpts?.manual, stepCount: steps.length },
      });

      const driverObj = driver({
        steps,
        showProgress: true,
        progressText: '{{current}} of {{total}}',
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: 'Finish',
        popoverClass: 'vp-tour-popover',
        smoothScroll: true,
        stagePadding: 8,
        stageRadius: 8,
        allowClose: true,
        overlayOpacity: 0.45,
        onNextClick: (_element, _step, { driver: d }) => {
          if (d.isLastStep()) {
            finishedRef.current = true;
            d.destroy();
            return;
          }
          d.moveNext();
        },
        onCloseClick: (_element, _step, { driver: d }) => {
          d.destroy();
        },
        onDestroyed: () => {
          driverRef.current = null;
          if (!userId || !orgId) return;

          if (finishedRef.current) {
            void markVendorPortalTourCompleted(userId, orgId);
            void logVendorPortalActivity(orgId, 'page_view', 'tour_completed', {
              metadata: { manual: !!startOpts?.manual },
            });
            return;
          }

          if (!prefs?.tourCompletedByOrg?.[orgId]) {
            void markVendorPortalTourSkipped(userId, orgId);
            void logVendorPortalActivity(orgId, 'page_view', 'tour_skipped', {
              metadata: { manual: !!startOpts?.manual },
            });
          }
        },
      });

      driverRef.current = driverObj;
      driverObj.drive();
      return true;
    },
    [destroyTour, orgId, prefs?.tourCompletedByOrg, projectCount, showCnc, showTimber, userId],
  );

  useEffect(() => () => destroyTour(), [destroyTour]);

  const canAutoStart =
    prefsLoaded &&
    portalReady &&
    realIsSupplier &&
    !readOnlyPreview &&
    !!userId &&
    !!orgId &&
    projectCount > 0 &&
    shouldAutoStartTour(prefs, orgId);

  useEffect(() => {
    if (!canAutoStart || autoStartedRef.current) return;
    autoStartedRef.current = true;
    const timer = window.setTimeout(() => {
      startTour({ manual: false });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [canAutoStart, startTour]);

  return { startTour };
}
